import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { api, type Me } from "../api";
import { BookMark } from "../components/icons";

const MAX_AGE = 120;
const MAX_AUTHOR_NAME_LEN = 120;

/**
 * First-run gate shown when the signed-in user has no author profile yet.
 * The two fields back the polish prompt (age) and the cover/library
 * "by …" line (name) — the rest of the app is unreachable until they're
 * filled in. Editable later from /account.
 *
 * We ask for age in the UI but store the *birth year* in the DB so the
 * stored value doesn't go stale — next year's prompts will see age+1
 * without anyone editing the profile.
 */
export default function Onboarding({ me, onSaved }: { me: Me; onSaved: (me: Me) => void }) {
  const { t } = useTranslation();
  const thisYear = new Date().getFullYear();
  const initialAge = me.authorBirthYear ? String(thisYear - me.authorBirthYear) : "";
  const [name, setName] = useState(me.authorName ?? "");
  const [age, setAge] = useState<string>(initialAge);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const trimmed = name.trim();
  const ageNum = Number(age);
  const ageOk = age !== "" && Number.isInteger(ageNum) && ageNum >= 0 && ageNum <= MAX_AGE;
  const canSave = trimmed.length > 0 && ageOk && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.updateMe({
        authorName: trimmed,
        authorBirthYear: thisYear - ageNum,
      });
      onSaved(updated);
    } catch (e2) {
      setErr(String(e2));
      setBusy(false);
    }
  };

  return (
    <div className="tp-app">
      <form className="tp-screen" onSubmit={submit} style={{ paddingTop: 36, paddingBottom: 32 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "inline-block", marginBottom: 12 }}>
            <BookMark />
          </div>
          <div className="tp-display" style={{ marginBottom: 8 }}>{t("onboarding.title")}</div>
          <div className="tp-body" style={{ fontSize: 17, lineHeight: 1.4, color: "var(--tp-ink-soft)", padding: "0 14px" }}>
            {t("onboarding.intro")}
          </div>
        </div>

        {me.credits?.balance != null && me.creditsPerPage != null && (
          <div
            style={{
              background: "var(--tp-surface-alt)",
              border: "2px solid var(--tp-ink)",
              borderRadius: "18px 14px 20px 16px / 14px 20px 14px 20px",
              padding: "12px 16px",
              marginBottom: 22,
              textAlign: "center",
              fontSize: 14,
              lineHeight: 1.45,
              color: "var(--tp-ink)",
            }}
          >
            🎟️{" "}
            <Trans
              i18nKey="onboarding.creditsNote"
              values={{ balance: me.credits.balance, perPage: me.creditsPerPage }}
              components={{ b: <strong style={{ color: "var(--tp-accent)" }} /> }}
            />
          </div>
        )}

        <div style={{ marginBottom: 18 }}>
          <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>
            {t("common.artistName")}
          </label>
          <input
            className="tp-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("onboarding.namePlaceholder")}
            maxLength={MAX_AUTHOR_NAME_LEN}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>
            {t("common.age")}
          </label>
          <input
            className="tp-input"
            type="number"
            inputMode="numeric"
            value={age}
            onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
            placeholder={t("onboarding.agePlaceholder")}
            min={0}
            max={MAX_AGE}
          />
          <div className="tp-body" style={{ fontSize: 12, color: "var(--tp-ink-soft)", marginTop: 6 }}>
            {t("onboarding.ageHint")}
          </div>
        </div>

        {err && <div className="tp-error">{err}</div>}

        <button type="submit" className="tp-btn tp-btn-primary tp-btn-block" disabled={!canSave}>
          {busy ? t("common.saving") : t("common.continue")}
        </button>

        <div className="tp-body" style={{ textAlign: "center", fontSize: 12, color: "var(--tp-ink-soft)", marginTop: 12 }}>
          {t("onboarding.editLater")}
        </div>
      </form>
    </div>
  );
}
